// src/api/client.js
//
// Shared axios instance for POG API calls. Single source for the JWT-injection
// request interceptor + 401-retry-with-refresh response interceptor.
//
// Why this exists:
//   `axios.create()` instances do NOT inherit interceptors from the default
//   axios instance. Before this file, 11 components/services each created
//   their own bare `axios.create({ baseURL })` and shipped requests without
//   the Authorization header — every page hit triggered a 401 storm against
//   the global JWT gate at backend/global_auth_gate.py. PR #379 mirrored
//   the active token onto axios.defaults.headers.common.Authorization as a
//   stop-gap; that fixed header injection but left three latent issues:
//     1. Cross-origin JWT leak risk (defaults header reaches every host).
//     2. No 401-retry-with-refresh on child instances — child 401s stayed.
//     3. Startup race — useEffects fire before the first
//        supabase.auth.getSession() Promise seeds the defaults header.
//   `api` solves all three.
//
// Use:
//   import { api } from '<relative>/api/client';
//   const { data } = await api.get('/api/products/<sku>');
//
// Public/cross-origin URLs (e.g. /vendor-reports/public/, third-party hosts)
// have any inherited Authorization header stripped before the request fires.
// Same auth behavior as the default axios instance — just on a child created
// via axios.create() so component-scoped instances no longer skip it.

import axios from 'axios';
import {
  getFreshAccessToken,
  refreshSessionOnce,
  isPublicUrl,
  isCrossOriginUrl,
} from './authHelpers';
// UI2 Teil 9, Z-1a: der geteilte Erreichbarkeits-Waechter. Der Antwort-Interceptor
// ist der EINE Ort, an dem jeder `api`-Aufruf vorbeikommt — ein 502/503/504 oder
// ein Netzfehler auf einem /api/-Pfad wird hier gezaehlt, eine erfolgreiche
// Antwort meldet den Dienst als erreichbar.
import { merkeFehlschlag, merkeErfolg } from './serviceOutage';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  const url = config.url || '';
  if (isPublicUrl(url) || isCrossOriginUrl(url)) {
    if (config.headers && config.headers.Authorization) {
      delete config.headers.Authorization;
    }
    return config;
  }
  try {
    const token = await getFreshAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch { /* unauthed — backend will 401 + response interceptor retries */ }
  return config;
});

api.interceptors.response.use(
  (response) => {
    merkeErfolg(response?.config?.url);
    return response;
  },
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    // Z-1a: VOR dem 401-Zweig zaehlen. Ein 401 ist kein Ausfall — `merkeFehlschlag`
    // verwirft ihn (nur 502/503/504 und Netzfehler zaehlen). Ein Netzfehler hat
    // `status == null` und wird als solcher gezaehlt.
    // 🔴 Der Fehler geht MIT: `status == null` trifft auch den Abbruch (ein
    // `signal`-Abbruch weist ohne Antwort ab), und `merkeFehlschlag` braucht das
    // Fehlerobjekt, um die beiden zu trennen.
    merkeFehlschlag(status, config?.url, error);
    if (status !== 401 || !config || config._pogRetry) {
      return Promise.reject(error);
    }
    const url = config.url || '';
    // NEVER retry public OR cross-origin URLs with a Bearer header — same
    // guard as the request interceptor. Without isCrossOriginUrl here, a
    // 401 on a future cross-origin call would leak the freshly-refreshed
    // JWT to the third-party host.
    if (isPublicUrl(url) || isCrossOriginUrl(url)) return Promise.reject(error);
    const refreshed = await refreshSessionOnce();
    if (!refreshed) return Promise.reject(error);
    config._pogRetry = true;
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${refreshed.access_token}`;
    return api(config);
  },
);
